<script>
  /*
   * Horizontal proportional bar with hover/tap tooltips per segment.
   *
   * Segments format:
   *   [{ label, value, color, hint? }, ...]
   *
   * - value is the £ amount (used both for width and for the tooltip text).
   * - color is a CSS colour or var reference (e.g. "var(--accent)").
   * - hint is optional supplementary text shown on the second line of the tooltip.
   *
   * Optional props:
   *   height:  bar height (default 14px)
   *   trackColor: bar background CSS colour (default var(--bg-2))
   *   variant: "light" | "dark" — controls tooltip contrast for dark headline cards
   */

  let {
    segments,
    height = 14,
    trackColor = "var(--bg-2)",
    variant = "light",
  } = $props();

  let hoveredIndex = $state(null);
  let wrapEl;

  const total = $derived(
    segments.reduce((s, seg) => s + Math.max(0, seg.value), 0),
  );

  function pctOf(v) {
    return total > 0 ? v / total : 0;
  }

  function handlePointer(e) {
    if (!wrapEl || segments.length === 0) return;
    const rect = wrapEl.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = rect.width > 0 ? x / rect.width : 0;
    let acc = 0;
    for (let i = 0; i < segments.length; i++) {
      const frac = total > 0 ? Math.max(0, segments[i].value) / total : 0;
      if (pct <= acc + frac) {
        hoveredIndex = i;
        return;
      }
      acc += frac;
    }
    hoveredIndex = segments.length - 1;
  }

  function handleLeave() {
    hoveredIndex = null;
  }

  const hovered = $derived(
    hoveredIndex != null ? segments[hoveredIndex] : null,
  );

  // Tooltip anchor: centre of the hovered segment on the bar.
  const tooltipLeftPct = $derived.by(() => {
    if (hoveredIndex == null || total === 0) return 0;
    let acc = 0;
    for (let i = 0; i < hoveredIndex; i++) {
      acc += Math.max(0, segments[i].value) / total;
    }
    const frac = Math.max(0, segments[hoveredIndex].value) / total;
    const midpoint = (acc + frac / 2) * 100;
    return Math.max(6, Math.min(94, midpoint));
  });

  const fmtGBP = (n) =>
    new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: 0,
    }).format(n);
  const fmtPct = (n) =>
    new Intl.NumberFormat("en-GB", {
      style: "percent",
      maximumFractionDigits: 1,
    }).format(n);
</script>

<div
  class="wrap"
  bind:this={wrapEl}
  onpointermove={handlePointer}
  onpointerdown={handlePointer}
  onpointerleave={handleLeave}
>
  <div
    class="bar"
    style="height: {height}px; background: {trackColor};"
    role="img"
    aria-label={segments.map((s) => `${s.label} ${fmtGBP(s.value)}`).join(", ")}
  >
    {#each segments as seg, i (i)}
      <div
        class="seg"
        class:hovered={hoveredIndex === i}
        style="flex-grow: {Math.max(0.0001, seg.value)}; background: {seg.color};"
      ></div>
    {/each}
  </div>

  {#if hovered != null}
    <div class="tooltip tooltip-{variant}" style="left: {tooltipLeftPct}%;">
      <div class="tt-swatch" style="background: {hovered.color};"></div>
      <div class="tt-body">
        <div class="tt-label">{hovered.label}</div>
        <div class="tt-value">
          {fmtGBP(hovered.value)}
          <span class="tt-pct">·  {fmtPct(pctOf(hovered.value))}</span>
        </div>
        {#if hovered.hint}
          <div class="tt-hint">{hovered.hint}</div>
        {/if}
      </div>
    </div>
  {/if}
</div>

<style>
  .wrap {
    position: relative;
    touch-action: pan-y;
    cursor: crosshair;
  }
  .bar {
    display: flex;
    border-radius: 6px;
    overflow: hidden;
  }
  .seg {
    transition: flex-grow 0.3s, filter 0.15s;
    min-width: 0;
  }
  .seg.hovered { filter: brightness(1.1); }

  .tooltip {
    position: absolute;
    top: calc(100% + 6px);
    transform: translateX(-50%);
    border-radius: 0.5rem;
    padding: 0.5rem 0.7rem;
    font-size: 0.82rem;
    min-width: 160px;
    max-width: 240px;
    pointer-events: none;
    z-index: 5;
    display: flex;
    gap: 0.55rem;
    align-items: flex-start;
    box-shadow: 0 6px 16px rgba(15, 61, 62, 0.15), 0 2px 4px rgba(15, 61, 62, 0.08);
  }
  .tooltip-light {
    background: var(--card);
    border: 1px solid var(--card-border);
    color: var(--fg);
  }
  .tooltip-dark {
    background: rgba(0, 0, 0, 0.85);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.15);
  }
  .tt-swatch {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    margin-top: 0.25rem;
    flex-shrink: 0;
  }
  .tt-body { flex: 1; min-width: 0; }
  .tt-label {
    font-weight: 700;
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    opacity: 0.85;
    margin-bottom: 0.15rem;
  }
  .tt-value {
    font-size: 1rem;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    line-height: 1.15;
  }
  .tt-pct {
    font-weight: 500;
    font-size: 0.82rem;
    opacity: 0.75;
  }
  .tt-hint {
    margin-top: 0.25rem;
    font-size: 0.75rem;
    opacity: 0.8;
    line-height: 1.4;
  }
</style>
